import {
  Button,
  CircularProgress,
  toast,
  Typography,
} from "@suraasa/placebo-ui"
import { useMutation } from "@tanstack/react-query"
import api from "api"
import {
  ActivityCategoryEnum,
  ActivityUserResponsePronunciation as ActivityUserResponse,
  PronunciationActivity,
} from "api/resources/learningItems/types"
import clsx from "clsx"
import AsyncBuilder from "components/AsyncBuilder"
import GenerateButton from "features/AItools/components/Generator/GenerationOutput/GenerateButton"
import { LearningModuleContext } from "features/LearningModule/context"
import { RefreshDouble } from "iconoir-react"
import { useCallback, useContext, useEffect, useState } from "react"
import { mapErrorsV2 } from "utils/helpers"

import ActivityContainer from "../ActivityContainer"
import { PRONUNCIATION_PASSING_SCORE } from "./constant"
import PronunciationComponent from "./PronunciationComponent"
import PronunciationFeedback from "./PronunciationFeedback"
import PronunciationSuccess from "./PronunciationSuccess"
import WaveForm from "./WaveForm"

const buildResponses = (
  data: PronunciationActivity["activity"]["activityItems"],
  responses: PronunciationActivity["attemptItemResponses"]
): ActivityUserResponse[] => {
  let file: ActivityUserResponse["responseFile"]
  return data.map(item => {
    const response = responses.find(r => r.activityItem === item.id)

    file = response?.responseFile || null

    const obj: ActivityUserResponse = {
      type: item.itemDetails.sentenceType,
      additionalDescription: item.itemDetails.additionalDescription,
      id: item.id,
      label: item.description,
      value: item.description,
      options: item.options,
      preRecordedFile: item.file,
      responseFile: response?.isCorrect === false ? null : file,
      wrongIndices: response?.responseDetails?.wrongIndices || [],
      overallClarityScore: response?.responseDetails?.overallClarityScore,
      validation: null,
      // validation:
      //   response?.isCorrect == null
      //     ? null
      //     : response.isCorrect
      //     ? "correct"
      //     : "incorrect",
    }

    return obj
  })
}

const calculatePercentage = (currentIndex: number, total: number) => {
  // const total = options.length
  // if (options.length === 0) {
  //   return 1
  // }

  // const answered = options.reduce((acc, item) => {
  //   if (
  //     item.overallClarityScore != null &&
  //     item.overallClarityScore >= PRONUNCIATION_PASSING_SCORE
  //   ) {
  //     return acc + 1
  //   }

  //   return acc
  // }, 0)

  const percentage = ((currentIndex + 1) / total) * 100
  return Math.max(percentage, 1)
}

const getInitialQuestionIndex = (responses: ActivityUserResponse[]) => {
  return Math.max(
    responses.findIndex(
      item =>
        item.overallClarityScore == null ||
        item.overallClarityScore < PRONUNCIATION_PASSING_SCORE
    ),
    0
  )
}

/**
 * Alternate UX:
 * Instead of making the activity timed, we can make it so that the user has to listen to the audio once, mandatory.
 * Then he can proceed to record his own audio.
 */

const Pronunciation = ({
  data,
  reset,
  onBack,
  category,
}: {
  onBack: () => void
  reset: () => Promise<void>
  data: PronunciationActivity
  category: ActivityCategoryEnum
}) => {
  const [responses, setResponses] = useState<ActivityUserResponse[]>([])

  const [isInaudibleAudio, setIsInaudibleAudio] = useState(false)

  const { switchItem } = useContext(LearningModuleContext)

  const responsesToAttempt = responses

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0)

  const isLastQuestion = currentQuestionIndex === responsesToAttempt.length - 1

  const lastQuestionIsPostCourseActivity =
    isLastQuestion && category === ActivityCategoryEnum.POST_COURSE
  const questionIsPreCourseActivity =
    isLastQuestion && category === ActivityCategoryEnum.PRE_COURSE

  const [changingQuestion, setChangingQuestion] = useState(false)

  const [mode, setMode] = useState<"completed" | "attempt">("attempt")

  const [audioBlobUrl, setAudioBlobUrl] = useState<{
    mpeg: string
    wav: string
  } | null>()

  useEffect(() => {
    setAudioBlobUrl(null)
    setIsInaudibleAudio(false)
  }, [currentQuestionIndex])

  useEffect(() => {
    const responses = buildResponses(
      data.activity.activityItems,
      data.attemptItemResponses
    )
    setResponses(responses)
    setCurrentQuestionIndex(getInitialQuestionIndex(responses))
  }, [data])

  const saveResponseOnBackend = useMutation({
    mutationFn: async ({
      activityItemId,
      responseFile,
    }: {
      activityItemId: number
      responseFile: File
    }) => {
      const files = new FormData()

      files.append("activity_item_id", activityItemId.toString())
      files.append("response_file", responseFile)
      return api.learningItems.activity.markAnswer({
        data: files,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        urlParams: {
          attemptId: data.id,
        },
      })
    },
    onSuccess: () => {
      // Ensuring that the feedback is visible on the screen
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        })
      }, 500)
    },
  })

  const submitActivity = useMutation({
    mutationFn: () =>
      api.learningItems.activity.submit({
        urlParams: {
          attemptId: data.id,
        },
      }),
    onSuccess: raw => {
      setCurrentQuestionIndex(0)

      const data = raw as PronunciationActivity
      setResponses(
        buildResponses(data.activity.activityItems, data.attemptItemResponses)
      )

      setMode("completed")
    },
    onError: e => mapErrorsV2(e),
  })

  const handleReset = async () => {
    await reset()
    setMode("attempt")
  }

  const item: ActivityUserResponse | undefined = responsesToAttempt[
    currentQuestionIndex
  ] as ActivityUserResponse | undefined

  const moveToNextQuestion = useCallback(() => {
    if (currentQuestionIndex === responsesToAttempt.length - 1) {
      return
    }

    setChangingQuestion(true)
    setTimeout(() => {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
      setChangingQuestion(false)
    }, 250)
  }, [currentQuestionIndex, responsesToAttempt.length])

  const moveToPreviousQuestion = useCallback(() => {
    if (currentQuestionIndex <= 0) {
      return 0
    }

    setChangingQuestion(true)
    setTimeout(() => {
      setCurrentQuestionIndex(currentQuestionIndex - 1)

      setChangingQuestion(false)
    }, 250)
  }, [currentQuestionIndex])

  const onSubmit = async () => {
    if (!item) return

    if (!audioBlobUrl) {
      toast.error("Please record an audio first before submitting")
      return
    }

    const fileContent: false | Blob = await fetch(audioBlobUrl.wav)
      .then(rs => rs.blob())
      .catch(() => false)
    if (!fileContent) {
      toast.error(
        "Cannot retrieve recorded audio. Please reset the recording and try again."
      )
      return
    }
    const file = new File([fileContent], "audio.wav")

    try {
      await saveResponseOnBackend.mutateAsync({
        activityItemId: item.id,
        responseFile: file,
      })
    } catch (err) {
      mapErrorsV2(err)
    }
  }

  const percentage = calculatePercentage(currentQuestionIndex, responses.length)

  const saveAnswer = useCallback(
    (blobUrl: { mpeg: string; wav: string }) => {
      if (!item) return

      setResponses(prev =>
        prev.map(x => {
          if (x.id === item.id) {
            return {
              ...x,
              responseFile: blobUrl.mpeg,
            }
          }

          return x
        })
      )
      setAudioBlobUrl(blobUrl)
    },
    [item]
  )

  const resetLocalStates = () => {
    setIsInaudibleAudio(false)
    setAudioBlobUrl(null)
    saveResponseOnBackend.reset()
  }

  const onContinue = useCallback(async () => {
    if (isLastQuestion) {
      try {
        await submitActivity.mutateAsync()
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        return
      }
    } else {
      moveToNextQuestion()
      resetLocalStates()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLastQuestion, moveToNextQuestion])

  const onInaudibleAudioDetected = () => {
    setIsInaudibleAudio(true)
  }

  if (changingQuestion)
    return (
      <ActivityContainer
        onBack={onBack}
        title={data.activity.title}
        progress={mode === "attempt" ? percentage : 100}
        endSlot={
          <Button
            color="black"
            disabled
            onClick={() => submitActivity.mutate()}
          >
            Submit
          </Button>
        }
      >
        <div className="flex items-center justify-center p-5">
          <CircularProgress />
        </div>
      </ActivityContainer>
    )

  return (
    <ActivityContainer
      onBack={onBack}
      title={data.activity.title}
      progress={mode === "attempt" ? percentage : 100}
    >
      {import.meta.env.MODE === "development" && mode === "attempt" && (
        <div className="flex items-center justify-between rounded-lg border border-onSurface-500 p-0.25">
          <Button size="sm" variant="text" onClick={moveToPreviousQuestion}>
            Previous
          </Button>
          <Typography variant="preTitle">
            This is for developers only
          </Typography>
          <Button size="sm" variant="text" onClick={moveToNextQuestion}>
            Next
          </Button>
        </div>
      )}

      {mode === "attempt" && item && (
        <div className="flex w-full flex-col items-stretch">
          <div className="flex flex-col">
            {category === ActivityCategoryEnum.IN_COURSE && (
              <>
                <Typography variant="largeBody" className="my-1">
                  Press the &apos;Record Now&apos; button, then recite the{" "}
                  {item.type}:
                </Typography>
                <Typography
                  variant="body"
                  className="mt-1 !leading-6 text-onSurface-500"
                >
                  <span className="mb-0.5">Please make sure that-</span>
                  <li className="ms-1">You speak clearly.</li>
                  <li className="ms-1">
                    You are in a quiet environment while recording.
                  </li>
                </Typography>
              </>
            )}

            {category === ActivityCategoryEnum.PRE_COURSE && (
              <>
                <Typography variant="largeBody" className="my-1">
                  Press the &apos;Record Now&apos; button, then recite the
                  passage:
                </Typography>
                <Typography
                  variant="body"
                  className="mt-1 !leading-6 text-onSurface-500"
                >
                  <span className="mb-0.5">Please make sure that-</span>
                  <li className="ms-1">You speak clearly.</li>
                  <li className="ms-1">
                    You are in a quiet environment when recording.
                  </li>
                </Typography>
              </>
            )}
            {category === ActivityCategoryEnum.POST_COURSE && (
              <>
                <Typography variant="largeBody" className="my-1">
                  Let’s see how your grasp on English has improved since you
                  started this course. Press the &apos;Record Now&apos; button,
                  then recite the paragraph:
                </Typography>
                <Typography
                  variant="body"
                  className="mt-1 !leading-6 text-onSurface-500"
                >
                  <span className="mb-0.5">Please make sure that-</span>
                  <li className="ms-1">You speak clearly.</li>
                  <li className="ms-1">
                    You are in a quiet environment when recording.
                  </li>
                </Typography>
              </>
            )}
          </div>
          <div className={clsx("mt-4 flex flex-col items-center")}>
            {category === ActivityCategoryEnum.POST_COURSE ? (
              <>
                {audioBlobUrl ? (
                  <PostCourse
                    postBlobURL={audioBlobUrl.mpeg}
                    onRetry={resetLocalStates}
                  />
                ) : (
                  <PronunciationComponent
                    onInaudibleAudioDetected={onInaudibleAudioDetected}
                    onRetry={resetLocalStates}
                    additionalDescription={item.additionalDescription}
                    text={item.label}
                    type={item.type}
                    preRecordedFile={item.preRecordedFile}
                    onRecordSuccess={saveAnswer}
                    hideWaveForm
                  />
                )}
              </>
            ) : (
              <>
                {mode === "attempt" && (
                  <PronunciationComponent
                    onInaudibleAudioDetected={onInaudibleAudioDetected}
                    onRetry={resetLocalStates}
                    additionalDescription={item.additionalDescription}
                    text={item.label}
                    type={item.type}
                    preRecordedFile={item.preRecordedFile}
                    onRecordSuccess={saveAnswer}
                  />
                )}
              </>
            )}

            {(lastQuestionIsPostCourseActivity ||
              questionIsPreCourseActivity) &&
              !saveResponseOnBackend.data &&
              audioBlobUrl && (
                <AsyncBuilder
                  handler={async () => {
                    try {
                      await onSubmit()
                      await submitActivity.mutateAsync()
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    } catch (error) {
                      return
                    }
                  }}
                  render={({ loading, onClick }) => (
                    <Button
                      color="black"
                      loading={loading}
                      label="Submit"
                      className="mt-2 w-fit"
                      onClick={onClick}
                    />
                  )}
                />
              )}

            {!lastQuestionIsPostCourseActivity &&
              !questionIsPreCourseActivity && (
                <AsyncBuilder
                  handler={onSubmit}
                  render={({ loading, onClick }) => {
                    if (audioBlobUrl) {
                      if (
                        !loading &&
                        !saveResponseOnBackend.data &&
                        !isInaudibleAudio
                      ) {
                        return (
                          <GenerateButton
                            loading={false}
                            label="Continue"
                            className="mt-2 w-fit"
                            onClick={onClick}
                          />
                        )
                      }
                    }
                    return (
                      <PronunciationFeedback
                        pronunciationFeedback={
                          saveResponseOnBackend?.data?.responseDetails
                        }
                        onRetry={() => {
                          resetLocalStates()
                          document.getElementById("retry-button")?.click()
                        }}
                        isLoading={loading}
                        isInaudibleAudio={isInaudibleAudio}
                        isLastQuestion={isLastQuestion}
                        onContinue={onContinue}
                      />
                    )
                  }}
                />
              )}
          </div>
        </div>
      )}

      {mode === "completed" && (
        <PronunciationSuccess
          onRetry={handleReset}
          onNext={() => {
            switchItem({ mode: "next" })
          }}
        />
      )}
    </ActivityContainer>
  )
}

export default Pronunciation

// const convertResponseToBlobURL = async (blob: Blob) => {
//   try {
//     const url = URL.createObjectURL(blob)
//     return url
//   } catch (error) {
//     console.error(error)
//   }
// }

const PostCourse = ({
  postBlobURL,
  onRetry,
}: {
  postBlobURL: string
  onRetry: () => void
}) => {
  const { plannerList } = useContext(LearningModuleContext)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const preCourse = plannerList?.find(
    item =>
      "activityCategory" in item &&
      item.activityCategory === ActivityCategoryEnum.PRE_COURSE
  )
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [blobUrl, setBlobUrl] = useState<string | null>(null)

  useEffect(() => {
    // const exec = async (activityId: number) => {
    //   try {
    //     const preActivity = await api.learningItems.activity.start({
    //       data: {
    //         activityId: activityId,
    //         retry: false,
    //       },
    //     })
    //     const preActivityResponseId = preActivity.attemptItemResponses[0].id
    //     const binary = await api.learningItems.activity.getItemResponse({
    //       urlParams: {
    //         itemResponseId: preActivityResponseId,
    //       },
    //     })
    //     console.log({ binary })
    //     if (binary) {
    //       const blobUrl = await convertResponseToBlobURL(binary)
    //       console.log({ blobUrl })
    //       if (blobUrl) setBlobUrl(blobUrl)
    //     }
    //   } catch (e) {
    //     console.error(e)
    //   }
    // }
    // if (preCourse) {
    //   exec(preCourse.learningContentId)
    // }
    // // Clean up the Blob URL when the component unmounts
    // return () => {
    //   if (blobUrl) {
    //     URL.revokeObjectURL(blobUrl)
    //   }
    // }
  }, [])

  return (
    <>
      {/* <Typography variant="title4" className="mb-2">
        Before you started the course
      </Typography>
      <WaveForm
        stream={blobUrl || undefined}
        height={40}
        width="100%"
        progressColor="#64748B"
        waveColor="#64748B"
        className="mb-3"
      /> */}

      <Typography variant="title4" className="mb-2">
        After you finished the course
      </Typography>
      <div className="flex w-full flex-wrap justify-end gap-1">
        <WaveForm
          stream={postBlobURL}
          height={40}
          width="100%"
          progressColor="#138D75"
          waveColor="#138D75"
          className="!bg-success-50"
        />
        <Button
          color="black"
          onClick={onRetry}
          className="!rounded-full !bg-black !px-1"
          size="sm"
        >
          <div className="flex items-center space-x-0.5 text-xs text-white">
            <RefreshDouble height={15} />
            <span>Retry</span>
          </div>
        </Button>
      </div>
    </>
  )
}
